import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "./constants";
import { modulePermission } from "./routePermissions";

/**
 * Feature → permission mapping used across nav, routes, and screens.
 *
 * Module ownership (product map):
 * 1. account      — Profile, notifications, password, /api/me/permissions
 * 2. events       — Programs, program details, pre/post deployment forms
 * 3. patients     — Patient registration in program details
 * 4. settings     — Org config, categories, form builder, bulk upload, notification groups
 * 5. staffing     — Reserved (not actively used in UI yet; assign flows still reference it)
 * 6. messages     — Chats, posts, tickets, bulk email, broadcast
 * 7. timesheet    — HTA attendance and timesheet section
 * 8. reports      — Dashboard, analytics
 * 9. inventory    — Equipment inventory
 * 10. mobile_units — Mobile unit section
 * 11. calendar    — Calendar events, Google meeting
 * 12. users       — Members, all users, field staff, invites, onboarding
 * 13. defects     — Defects list, create, view, edit
 * 14. work_orders — Work orders list, create, view, edit
 *
 * Actions per module: view | view_own | create | edit
 * delete / deactivate resolve to edit (EDIT_IMPLIED_ACTIONS) — show for admin with *.edit
 */
export const FEATURE_PERMISSIONS = {
  SEE_MY_PROGRAMS: { module: PERMISSION_MODULES.EVENTS, action: PERMISSION_ACTIONS.VIEW_OWN },
  SEE_ALL_PROGRAMS: { module: PERMISSION_MODULES.EVENTS, action: PERMISSION_ACTIONS.VIEW },
  /** Nav: programs list (my OR all) */
  PROGRAMS_MENU: modulePermission.view(PERMISSION_MODULES.EVENTS),

  MY_CALENDAR: { module: PERMISSION_MODULES.CALENDAR, action: PERMISSION_ACTIONS.VIEW_OWN },
  ALL_CALENDAR: { module: PERMISSION_MODULES.CALENDAR, action: PERMISSION_ACTIONS.VIEW },
  /** Nav: calendar (my OR all) */
  CALENDAR_MENU: modulePermission.view(PERMISSION_MODULES.CALENDAR),

  MY_MOBILE_UNITS: {
    module: PERMISSION_MODULES.MOBILE_UNITS,
    action: PERMISSION_ACTIONS.VIEW_OWN,
  },
  ALL_MOBILE_UNITS: {
    module: PERMISSION_MODULES.MOBILE_UNITS,
    action: PERMISSION_ACTIONS.VIEW,
  },
  /** Nav: mobile unit management (my OR all) */
  MOBILE_UNITS_MENU: modulePermission.view(PERMISSION_MODULES.MOBILE_UNITS),
  EDIT_FLEET: {
    module: PERMISSION_MODULES.MOBILE_UNITS,
    action: PERMISSION_ACTIONS.EDIT,
  },

  APPROVE_WORK_ORDER: {
    module: PERMISSION_MODULES.SETTINGS,
    action: PERMISSION_ACTIONS.EDIT,
  },
  CATEGORIES: {
    module: PERMISSION_MODULES.SETTINGS,
    action: PERMISSION_ACTIONS.VIEW,
  },

  /**
   * Bulk upload hub: must have settings.view AND (settings.create OR settings.edit)
   */
  BULK_UPLOAD: {
    all: [
      { module: PERMISSION_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
      {
        any: [
          { module: PERMISSION_MODULES.SETTINGS, action: PERMISSION_ACTIONS.CREATE },
          { module: PERMISSION_MODULES.SETTINGS, action: PERMISSION_ACTIONS.EDIT },
        ],
      },
    ],
  },

  EXPORT_EVENTS_CALENDAR: [
    { module: PERMISSION_MODULES.REPORTS, action: PERMISSION_ACTIONS.VIEW },
    { module: PERMISSION_MODULES.REPORTS, action: PERMISSION_ACTIONS.CREATE },
  ],

  IMPORT_EVENT_SPREADSHEET: [
    { module: PERMISSION_MODULES.SETTINGS, action: PERMISSION_ACTIONS.EDIT },
    { module: PERMISSION_MODULES.EVENTS, action: PERMISSION_ACTIONS.CREATE },
  ],

  MEMBERS_LIST: {
    module: PERMISSION_MODULES.USERS,
    action: PERMISSION_ACTIONS.VIEW,
  },
  ADD_USER: {
    module: PERMISSION_MODULES.USERS,
    action: PERMISSION_ACTIONS.CREATE,
  },
  MANAGE_PERMISSIONS: {
    module: PERMISSION_MODULES.USERS,
    action: PERMISSION_ACTIONS.EDIT,
  },

  DASHBOARD_REPORTS: {
    module: PERMISSION_MODULES.REPORTS,
    action: PERMISSION_ACTIONS.VIEW,
  },

  STAFFING_VIEW: {
    module: PERMISSION_MODULES.STAFFING,
    action: PERMISSION_ACTIONS.VIEW,
  },
  STAFFING_EDIT: {
    module: PERMISSION_MODULES.STAFFING,
    action: PERMISSION_ACTIONS.EDIT,
  },
  /** Admin staffing nominations: view OR edit */
  STAFFING_NOMINATIONS: modulePermission.view(PERMISSION_MODULES.STAFFING).concat([
    { module: PERMISSION_MODULES.STAFFING, action: PERMISSION_ACTIONS.EDIT },
  ]),

  CHANGE_PASSWORD: {
    module: PERMISSION_MODULES.ACCOUNT,
    action: PERMISSION_ACTIONS.EDIT,
  },

  /** Nav/routes: defects list & detail (defects.view | defects.view_own) */
  DEFECTS_MENU: modulePermission.view(PERMISSION_MODULES.DEFECTS),
  DEFECTS_CREATE: modulePermission.create(PERMISSION_MODULES.DEFECTS),
  DEFECTS_EDIT: modulePermission.edit(PERMISSION_MODULES.DEFECTS),
  /** Nav/routes: work orders list & detail (work_orders.view | work_orders.view_own) */
  WORK_ORDERS_MENU: modulePermission.view(PERMISSION_MODULES.WORK_ORDERS),
  WORK_ORDERS_CREATE: modulePermission.create(PERMISSION_MODULES.WORK_ORDERS),
  WORK_ORDERS_EDIT: modulePermission.edit(PERMISSION_MODULES.WORK_ORDERS),
};

/**
 * Nav visibility MUST mirror privateRoutes `permission` for the same path.
 * Use these in Layout / MobileDrawer so menus never show when the route will block.
 */
export const NAV_ROUTE_PERMISSIONS = {
  /** /dashboard, /analytics */
  HOME: FEATURE_PERMISSIONS.DASHBOARD_REPORTS,
  ANALYTICS: FEATURE_PERMISSIONS.DASHBOARD_REPORTS,
  /** /programs */
  PROGRAMS: FEATURE_PERMISSIONS.PROGRAMS_MENU,
  /** /calendar */
  CALENDAR: FEATURE_PERMISSIONS.CALENDAR_MENU,
  /** /discussions */
  MESSAGES: modulePermission.view(PERMISSION_MODULES.MESSAGES),
  /** timesheet routes */
  TIMESHEET: modulePermission.view(PERMISSION_MODULES.TIMESHEET),
  /** /members — users.view only (not view_own) */
  MEMBERS_LIST: FEATURE_PERMISSIONS.MEMBERS_LIST,
  /** /members/add */
  ADD_USER: FEATURE_PERMISSIONS.ADD_USER,
  /** /equipment */
  MOBILE_UNITS_LIST: FEATURE_PERMISSIONS.MOBILE_UNITS_MENU,
  /** /mobileunit/add */
  ADD_MOBILE_UNIT: modulePermission.create(PERMISSION_MODULES.MOBILE_UNITS),
  /** /category */
  CATEGORIES: FEATURE_PERMISSIONS.CATEGORIES,
  /** /bulk-upload */
  BULK_UPLOAD: FEATURE_PERMISSIONS.BULK_UPLOAD,
  /** /permission-management */
  MANAGE_PERMISSIONS: FEATURE_PERMISSIONS.MANAGE_PERMISSIONS,
  /** /audit-logs — admin-only settings view */
  AUDIT_LOGS: modulePermission.view(PERMISSION_MODULES.SETTINGS),
  /** /notification-group, /form_builder */
  SETTINGS_VIEW: modulePermission.view(PERMISSION_MODULES.SETTINGS),
  /** /defects, /defects/view/:id */
  DEFECTS: FEATURE_PERMISSIONS.DEFECTS_MENU,
  /** /defects/create */
  DEFECTS_CREATE: FEATURE_PERMISSIONS.DEFECTS_CREATE,
  /** /work-orders, /work-orders/view/:id */
  WORK_ORDERS: FEATURE_PERMISSIONS.WORK_ORDERS_MENU,
  /** /work-orders/create */
  WORK_ORDERS_CREATE: FEATURE_PERMISSIONS.WORK_ORDERS_CREATE,
  /** /work-orders/edit/:id */
  WORK_ORDERS_EDIT: FEATURE_PERMISSIONS.WORK_ORDERS_EDIT,
  /** /mobile-inventory, /mobile-maintenance, /inventory-counts */
  INVENTORY: modulePermission.view(PERMISSION_MODULES.INVENTORY),
};
